import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import {
  createAiChatSession,
  deleteAiChatSession,
  getAiChatSession,
  listAiChatSessions,
  sendAiChatMessage,
} from '../../clients/ai_chat_client';
import { showToast } from '../../config_and_helpers/helpers';
import { materiahAiIcon } from '../../config_and_helpers/config';
import { logError, logInfo } from '../../config_and_helpers/logger';
import AiChatComposer from './AiChatComposer';
import AiChatSessionList from './AiChatSessionList';
import AiChatTranscript from './AiChatTranscript';
import './AiChatStyle.css';

const LOG_CONTEXT = 'AiChatPanel';

/**
 * Open drawer panel for Materiah AI Q&A (sessions + transcript + composer).
 *
 * @param {Object} props
 * @param {function} props.onClose - Returns UI to the collapsed round FAB.
 * @returns {JSX.Element}
 */
const AiChatPanel = ({ onClose }) => {
  const { token } = useContext(AppContext);

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [sending, setSending] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [initError, setInitError] = useState(null);

  const initOnceRef = useRef(false);

  const applyUnavailable = useCallback(status => {
    if (status === 503) {
      setUnavailable(true);
      return true;
    }
    return false;
  }, []);

  const loadSessionDetail = useCallback(
    async sessionId => {
      if (!token || !sessionId) return;
      setLoadingTranscript(true);
      logInfo(LOG_CONTEXT, 'loadSessionDetail start', { sessionId });
      const result = await getAiChatSession(token, sessionId);
      if (!result.success) {
        applyUnavailable(result.status);
        showToast(result.userMessage, 'error', 'top-center');
        logError(LOG_CONTEXT, 'loadSessionDetail failed', {
          sessionId,
          status: result.status,
        });
        setLoadingTranscript(false);
        return;
      }
      setUnavailable(false);
      setActiveSessionId(result.data.id);
      setMessages(result.data.messages || []);
      setLoadingTranscript(false);
      logInfo(LOG_CONTEXT, 'loadSessionDetail success', {
        sessionId: result.data.id,
        messageCount: result.data.messages?.length,
      });
    },
    [token, applyUnavailable]
  );

  const bootstrap = useCallback(async () => {
    if (!token) return;
    setLoadingSessions(true);
    setInitError(null);
    logInfo(LOG_CONTEXT, 'bootstrap start', {});

    const listResult = await listAiChatSessions(token);
    if (!listResult.success) {
      applyUnavailable(listResult.status);
      setInitError(listResult.userMessage);
      showToast(listResult.userMessage, 'error', 'top-center');
      setLoadingSessions(false);
      logError(LOG_CONTEXT, 'bootstrap list failed', {
        status: listResult.status,
      });
      return;
    }

    setUnavailable(false);
    let nextSessions = listResult.results || [];

    if (nextSessions.length === 0) {
      const created = await createAiChatSession(token);
      if (!created.success) {
        applyUnavailable(created.status);
        setInitError(created.userMessage);
        showToast(created.userMessage, 'error', 'top-center');
        setLoadingSessions(false);
        logError(LOG_CONTEXT, 'bootstrap create failed', {
          status: created.status,
        });
        return;
      }
      nextSessions = [
        {
          id: created.data.id,
          title: created.data.title,
          status: created.data.status,
          created_at: created.data.created_at,
          updated_at: created.data.updated_at,
          message_count: created.data.messages?.length || 0,
        },
      ];
      setSessions(nextSessions);
      setLoadingSessions(false);
      setActiveSessionId(created.data.id);
      setMessages(created.data.messages || []);
      logInfo(LOG_CONTEXT, 'bootstrap created empty session', {
        sessionId: created.data.id,
      });
      return;
    }

    setSessions(nextSessions);
    setLoadingSessions(false);
    const firstId = nextSessions[0].id;
    await loadSessionDetail(firstId);
    logInfo(LOG_CONTEXT, 'bootstrap success', {
      sessionCount: nextSessions.length,
      activeSessionId: firstId,
    });
  }, [token, applyUnavailable, loadSessionDetail]);

  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;
    bootstrap();
  }, [bootstrap]);

  const handleNewChat = async () => {
    if (!token || sending) return;
    logInfo(LOG_CONTEXT, 'handleNewChat start', {});
    const created = await createAiChatSession(token, {
      title: 'New chat',
    });
    if (!created.success) {
      applyUnavailable(created.status);
      showToast(created.userMessage, 'error', 'top-center');
      return;
    }
    setUnavailable(false);
    const row = {
      id: created.data.id,
      title: created.data.title,
      status: created.data.status,
      created_at: created.data.created_at,
      updated_at: created.data.updated_at,
      message_count: created.data.messages?.length || 0,
    };
    setSessions(prev => [row, ...prev]);
    setActiveSessionId(created.data.id);
    setMessages(created.data.messages || []);
    setDraft('');
  };

  const handleArchive = async sessionId => {
    if (!token || sending) return;
    logInfo(LOG_CONTEXT, 'handleArchive start', { sessionId });
    const result = await deleteAiChatSession(token, sessionId);
    if (!result.success) {
      applyUnavailable(result.status);
      showToast(result.userMessage, 'error', 'top-center');
      return;
    }
    showToast('Chat archived', 'success', 'top-center');
    const remaining = sessions.filter(s => String(s.id) !== String(sessionId));
    setSessions(remaining);
    if (String(activeSessionId) === String(sessionId)) {
      if (remaining.length > 0) {
        await loadSessionDetail(remaining[0].id);
      } else {
        await handleNewChat();
      }
    }
  };

  const handleSend = async () => {
    const content = draft.trim();
    if (!token || !activeSessionId || !content || sending) return;

    const tempUserId = `local-user-${Date.now()}`;
    const optimisticUser = {
      id: tempUserId,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
      citations: [],
      tool_trace: [],
    };

    setSending(true);
    setDraft('');
    setMessages(prev => [...prev, optimisticUser]);
    logInfo(LOG_CONTEXT, 'send start', {
      sessionId: activeSessionId,
      contentLength: content.length,
    });

    const result = await sendAiChatMessage(token, activeSessionId, content);

    if (!result.success) {
      applyUnavailable(result.status);
      showToast(result.userMessage, 'error', 'top-center');
      setMessages(prev => prev.filter(m => m.id !== tempUserId));
      setDraft(content);
      setSending(false);
      logError(LOG_CONTEXT, 'send failed', {
        sessionId: activeSessionId,
        status: result.status,
      });
      return;
    }

    setUnavailable(false);
    const assistant = result.data.message;
    setMessages(prev => [...prev, assistant]);
    setSessions(prev =>
      prev.map(s =>
        String(s.id) === String(activeSessionId)
          ? {
              ...s,
              message_count: (s.message_count || 0) + 2,
              updated_at: assistant?.created_at || s.updated_at,
              title:
                s.title && s.title !== 'New chat'
                  ? s.title
                  : content.slice(0, 48) || s.title,
            }
          : s
      )
    );
    setSending(false);
    logInfo(LOG_CONTEXT, 'send success', {
      sessionId: activeSessionId,
      messageId: assistant?.id,
      citationCount: assistant?.citations?.length,
    });
  };

  const busy = loadingSessions || loadingTranscript || sending;

  return (
    <div className="ai-chat-panel" role="dialog" aria-label="Materiah AI">
      <header className="ai-chat-panel__header">
        <div className="ai-chat-panel__title-wrap">
          <img
            src={materiahAiIcon}
            alt=""
            className="ai-chat-panel__title-icon"
            width={40}
            height={40}
            draggable={false}
          />
          <h2>Materiah AI</h2>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onClose}
          aria-label="Close Materiah AI"
        >
          Close
        </Button>
      </header>

      {(unavailable || initError) && (
        <Alert variant="warning" className="ai-chat-unavailable">
          {unavailable
            ? 'Assistant unavailable'
            : initError || 'Unable to load assistant'}
        </Alert>
      )}

      {loadingSessions && sessions.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 p-4">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <div className="ai-chat-panel__body">
          <AiChatSessionList
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={loadSessionDetail}
            onNewChat={handleNewChat}
            onArchive={handleArchive}
            disabled={busy || unavailable}
          />
          <div className="ai-chat-main">
            {loadingTranscript ? (
              <div className="d-flex justify-content-center align-items-center flex-grow-1">
                <Spinner animation="border" size="sm" role="status" />
              </div>
            ) : (
              <AiChatTranscript
                messages={messages}
                isAwaitingReply={sending}
              />
            )}
            <AiChatComposer
              value={draft}
              onChange={setDraft}
              onSend={handleSend}
              disabled={!activeSessionId || unavailable || loadingTranscript}
              sending={sending}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AiChatPanel;


